import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Groups from 'src/constants/Groups';
import { selectFacilityId } from 'src/store/configSlice';
import { useGetGroupsQuery } from 'src/store/sageAdminApi';

interface UseFacilityGroupsProps {
    facilityId?: string;
}

function useFacilityGroups(props: UseFacilityGroupsProps = {}) {
    const { facilityId } = props;

    const storeFacilityId = useSelector(selectFacilityId);

    const searchFacilityId = facilityId || storeFacilityId;

    const { data: facilityGroups, ...rest } = useGetGroupsQuery(
        { facilityId: searchFacilityId },
        { skip: !searchFacilityId }
    );

    const { groupIdMap, groupNameMap } = useMemo(
        () => ({
            groupIdMap: new Map(facilityGroups?.map((group) => [group.id, group]) || []),
            groupNameMap: new Map(facilityGroups?.map((group) => [group.name, group]) || []),
        }),
        [facilityGroups]
    );

    const { careGroup, nurseMedtechGroup, sageSupportGroup, managementGroup } = useMemo(
        () => ({
            careGroup: groupNameMap.get(Groups.CareStaff),
            nurseMedtechGroup: groupNameMap.get(Groups.NurseMedTech),
            sageSupportGroup: groupNameMap.get(Groups.SageSupport),
            managementGroup: groupNameMap.get(Groups.Management),
        }),
        [groupNameMap]
    );

    return {
        ...rest,
        facilityGroups: facilityGroups || [],
        groupIdMap,
        groupNameMap,

        careGroup,
        nurseMedtechGroup,
        sageSupportGroup,
        managementGroup,
    };
}

export default useFacilityGroups;
